import { api } from "@/api/client";

export interface BillLine {
  line_type?: "item" | "expense";
  description?: string;
  product_code?: string | null;
  quantity?: number | null;
  rate?: number | null;
  amount?: number | null;
  item_id?: number | string | null;
  account_id?: number | string | null; // expense lines
  department_id?: number | string | null;
  class_id?: number | string | null;
  location_id?: number | string | null;
  customer_id?: number | string | null; // NetSuite Project (job) the line is coded to
  createdfrom?: number | string | null;
  po_line_id?: number | string | null;
}

export interface BillHeader {
  vendor_name?: string;
  invoice_number?: string;
  trandate?: string;
  due_date?: string;
  terms?: string; // as printed on the invoice
  terms_id?: number | string | null; // NetSuite term posted on the bill
  po_number?: string;
  memo?: string;
  currency?: string;
  subsidiary_id?: number | string | null;
  department_id?: number | string | null;
  class_id?: number | string | null;
  location_id?: number | string | null;
  approver_id?: number | string | null;
  // Hints Claude extracts that drive class/location coding.
  branch?: string | null;
  job_type?: string | null;
  // NetSuite Project coding: the project number as read (project_id), its
  // normalized entityid, and the resolved job internal id (applied to lines).
  project_id?: string | null;
  project_entityid?: string | null;
  project_internal_id?: number | string | null;
}

export interface BillDraftContent {
  transaction_kind?: string;
  header: BillHeader;
  lines: BillLine[];
  totals?: { amount?: number | null };
  matched_po_id?: number | null;
}

export interface Validation {
  errors: string[];
  warnings: string[];
  valid: boolean;
  open_pos?: OpenPo[];
}

// A vendor's open PO the server Validator turned up. "project": open on this
// bill's project (a received one blocks posting stand-alone). "job_hint": a
// received PO listed to help pick a contract-labor bill's job.
export interface OpenPo {
  kind: "project" | "job_hint";
  netsuite_id: number;
  tranid: string | null;
  status: string | null;
  status_name: string;
  received: boolean;
  total: number | null;
  project_ids: number[];
}

// State of the matched purchase order in reviewer terms. NetSuite refuses to bill
// a PO with nothing received against it, so pending_receipt is the flag the editor
// warns on (and the submitter checks live) BEFORE the bill is attempted.
export interface PoStatus {
  netsuite_id: number;
  tranid: string | null;
  status: string | null;        // NetSuite code: B Pending Receipt, D/E partially received, F Pending Bill…
  status_name: string;
  pending_receipt: boolean;
  checked_at?: string | null;
  source?: "mirror" | "netsuite";
  error?: string | null;        // set when a live check failed and the mirror value is shown instead
}

// An existing bill the duplicate check thinks this one may repeat (see
// NetsuiteBills::DuplicateDetector). "strong" = same normalized invoice number;
// "overlap" = a line repeats one on an existing bill for the same project (item
// and quantity, or expense account and amount); "possible" = same amount with a
// date within 7 days.
export interface DuplicateCandidate {
  source: "app" | "netsuite";
  match: "strong" | "overlap" | "possible";
  reasons: string[];
  // Lines of this bill that repeat a line on that bill (overlap rule).
  overlap_lines?: number;
  draft_id: number | null;
  draft_status?: string | null;
  netsuite_id: number | null;
  record_type: "vendor_bill" | "bill_credit";
  tranid: string | null;
  trandate: string | null;
  amount: number | null;
  memo: string | null;
  status?: string | null;
  external_id: string | null;
  entered_via: "ap_triage" | "manual";
  entered_by: string | null;
  created_at: string | null;
  netsuite_url: string | null;
  // Strong matches remember the normalized invoice # they matched on. One
  // carried over after the invoice # was edited is flagged invoice_changed.
  matched_invoice_key?: string | null;
  invoice_changed?: boolean;
}

// Same rule as DuplicateDetector.normalize_invoice on the server.
export function normalizeInvoice(v: string | null | undefined): string {
  return (v ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "").replace(/^0+/, "");
}

// Mirrors DuplicateDetector::Result#blocking: candidates that need the
// "not a duplicate" confirmation before posting. Strong and overlap matches
// always; with no invoice number, possible matches too. Other possible matches
// only warn.
export function blockingCandidates(
  candidates: DuplicateCandidate[] | undefined, invoiceNumber: string | null | undefined,
): DuplicateCandidate[] {
  const noInvoice = normalizeInvoice(invoiceNumber) === "";
  return (candidates ?? []).filter((c) => c.match === "strong" || c.match === "overlap" || (noInvoice && c.match === "possible"));
}

export type DuplicateCheckStatus = "ok" | "check_unavailable" | null;

// One existing NetSuite bill read live for the comparison dialog.
export interface ExistingBill {
  header: {
    netsuite_id: number; record_type: string; tranid: string | null; trandate: string | null;
    due_date: string | null; amount: number | null; memo: string | null; vendor_id: number | null;
    vendor_name: string | null; status: string | null; external_id: string | null;
    entered_via: "ap_triage" | "manual";
  };
  lines: Array<{
    line: number | null; item_id: number | null; item_name: string | null; account_id: number | null;
    account_name: string | null; description: string | null; quantity: number | null; rate: number | null;
    amount: number | null; department: string | null; class_name: string | null; location: string | null;
  }>;
}

export interface VendorBillDraft {
  id: number;
  status: string;
  transaction_kind: string;
  netsuite_vendor_id: number | null;
  vendor_rule_id: number | null;
  triage_decision_id: number | null;
  netsuite_bill_id: number | null;
  netsuite_tranid: string | null;
  netsuite_url: string | null;
  // File Cabinet id of the source PDF once uploaded + attached to the bill.
  netsuite_file_id: number | null;
  source_pdf_attached: boolean;
  error_message: string | null;
  // Set when this draft is one invoice sliced out of a multi-invoice PDF.
  source_page_start: number | null;
  source_page_end: number | null;
  source_attachment_id?: string | null;
  created_at: string;
  updated_at: string;
  ocr_status: string | null;
  pdf_url: string | null;
  // Set when pdf_url was rendered from the vendor's Excel/CSV/Word file.
  original_url?: string | null;
  original_filename?: string | null;
  draft: BillDraftContent;
  ai_output?: BillDraftContent;
  validation?: Validation;
  submitted_at?: string | null;
  posted_at?: string | null;
  submitted_by?: { id: number; email: string } | null;
  created_by?: { id: number; email: string } | null;
  duplicate_candidates?: DuplicateCandidate[];
  duplicate_check_status?: DuplicateCheckStatus;
  duplicate_checked_at?: string | null;
  duplicate_confirmed_at?: string | null;
  duplicate_confirmed_by?: { id: number; email: string } | null;
  // Set on create when an existing draft for the same invoice came back
  // instead of a new one (posted or failed drafts are no longer silently
  // replaced, which is how the same invoice used to post twice).
  existing_draft?: "posted" | "failed" | "open";
  submissions?: Array<{
    id: number; succeeded: boolean; response_status: number | null;
    netsuite_bill_id: number | null; created_at: string;
  }>;
  // Status of the matched PO (mirror) so the editor can show it on load.
  matched_po?: PoStatus | null;
  // Present on an update that requested a PO match.
  po_match?: { requested: string; matched: boolean; tranid?: string | null; lines?: number; po?: PoStatus | null };
}

// The draft is "settling" while OCR + Claude structuring run in the background.
export function isDraftSettling(d: VendorBillDraft): boolean {
  return ["created", "ocr_running", "ocr_done", "structuring"].includes(d.status);
}

export async function createBillDraft(
  decisionId: number, attachmentId?: string, pageRange?: { start: number; end: number }, forceNew = false,
): Promise<VendorBillDraft> {
  const { data } = await api.post<VendorBillDraft>("/vendor_bill_drafts", {
    decision_id: decisionId,
    attachment_id: attachmentId,
    page_range: pageRange,
    force_new: forceNew || undefined,
  });
  return data;
}

// Router state the bill page reads to explain why it opened an existing draft.
export interface BillPageState { existingDraft?: VendorBillDraft["existing_draft"] }
export function billPageState(draft: VendorBillDraft): BillPageState {
  return draft.existing_draft ? { existingDraft: draft.existing_draft } : {};
}

export async function fetchBillDraft(id: number): Promise<VendorBillDraft> {
  const { data } = await api.get<VendorBillDraft>(`/vendor_bill_drafts/${id}`);
  return data;
}

export async function updateBillDraft(
  id: number, draft: BillDraftContent, poNumber?: string, netsuiteVendorId?: number | null, removePo?: boolean,
  postStandalone?: boolean, varianceOverrideReason?: string,
): Promise<VendorBillDraft> {
  const body: Record<string, unknown> = { draft };
  if (poNumber) body.po_number = poNumber;
  // "Remove PO": the server drops matched_po_id, the header PO # and each line's
  // PO link, so the bill posts as a plain bill.
  if (removePo) body.remove_po = true;
  // "Post stand-alone anyway": the server records the received open POs this
  // bill is not billing, and who decided, for the approver.
  if (postStandalone) body.post_standalone = true;
  // "Post anyway" over the PO variance block: the reviewer's reason, which the
  // server records for this PO and amount and puts in the memo and approver note.
  if (varianceOverrideReason) body.variance_override_reason = varianceOverrideReason;
  // Only sent when the caller passes it (repointing the vendor). Left undefined
  // for a plain save so netsuite_vendor_id is never touched server-side.
  if (netsuiteVendorId !== undefined) body.netsuite_vendor_id = netsuiteVendorId;
  const { data } = await api.patch<VendorBillDraft>(`/vendor_bill_drafts/${id}`, body);
  return data;
}

// confirmed: the duplicate candidates the reviewer compared before choosing
// "Not a duplicate, post anyway". The server only honors the confirm if these
// cover every strong candidate its fresh check finds; otherwise it answers 409
// again with the current list. Omit for a normal submit.
export async function submitBillDraft(id: number, confirmed?: DuplicateCandidate[]): Promise<VendorBillDraft> {
  const body = confirmed
    ? {
        confirm_duplicate: true,
        confirmed_candidates: confirmed.map((c) => ({ netsuite_id: c.netsuite_id, draft_id: c.draft_id })),
      }
    : undefined;
  const { data } = await api.post<VendorBillDraft>(`/vendor_bill_drafts/${id}/submit`, body);
  return data;
}

// The 409 body BillSubmitter answers for a likely duplicate.
export interface PossibleDuplicateError {
  error: string;
  code: "possible_duplicate";
  duplicate_candidates: DuplicateCandidate[];
  duplicate_check_status: DuplicateCheckStatus;
}

export function possibleDuplicateFrom(e: unknown): PossibleDuplicateError | null {
  const r = (e as { response?: { status?: number; data?: Partial<PossibleDuplicateError> } })?.response;
  if (r?.status === 409 && r.data?.code === "possible_duplicate") return r.data as PossibleDuplicateError;
  return null;
}

export async function fetchDuplicateCandidate(draftId: number, netsuiteId: number): Promise<ExistingBill> {
  const { data } = await api.get<ExistingBill>(`/vendor_bill_drafts/${draftId}/duplicate_candidates/${netsuiteId}`);
  return data;
}

// Ask NetSuite live whether the matched PO has been received yet (folds the
// answer into the mirror). Used by "Check again" once ops says they received it.
export async function refreshPoStatus(id: number): Promise<PoStatus> {
  const { data } = await api.post<PoStatus>(`/vendor_bill_drafts/${id}/refresh_po_status`);
  return data;
}

export function billPdfUrl(id: number): string {
  return `/api/v1/vendor_bill_drafts/${id}/pdf`;
}

// The Class + Location a project id drives (via this draft's vendor rule and the
// project's Sunrise market). null fields mean "leave the current value as-is".
export interface ProjectCoding {
  job_type: string | null;
  class_id: number | null;
  class_name: string | null;
  location_id: number | null;
  location_name: string | null;
}

export async function deriveProjectCoding(id: number, project: string): Promise<ProjectCoding> {
  const { data } = await api.get<ProjectCoding>(`/vendor_bill_drafts/${id}/project_coding`, { params: { project } });
  return data;
}
